import { fetchAuthSession } from 'aws-amplify/auth';

// Empty string in dev = relative URLs, proxied by CRA dev server to dev.api.sahayy.com
// Full URL in prod = direct request to api.sahayy.com
export const API_BASE = process.env.REACT_APP_API_BASE ?? 'https://api.sahayy.com';

async function getAuthToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() || null;
  } catch {
    return null;
  }
}

/**
 * GET request.
 * @param auth - if true, throws when no token; if false, still attaches token when available
 */
export async function apiGet<T>(
  path: string,
  params?: Record<string, string>,
  auth = false
): Promise<T> {
  const token = await getAuthToken();

  if (auth && !token) {
    throw new Error('Authentication required');
  }

  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = token;

  const base = API_BASE || window.location.origin;
  const url = new URL(base + path);
  if (params) {
    Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
  }

  const res = await fetch(url.toString(), { headers });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`GET ${path} failed: ${res.status} ${text}`);
  }
  return res.json() as Promise<T>;
}

/**
 * POST request.
 * @param auth - if true, throws when no token; if false, still attaches token when available
 */
export async function apiPost<T>(
  path: string,
  body: unknown,
  auth = false
): Promise<T> {
  const token = await getAuthToken();

  if (auth && !token) {
    throw new Error('Authentication required');
  }

  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = token;

  const res = await fetch((API_BASE || window.location.origin) + path, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`POST ${path} failed: ${res.status} ${text}`);
  }
  return res.json() as Promise<T>;
}

export async function apiPut<T>(
  path: string,
  body: unknown,
  params?: Record<string, string>,
): Promise<T> {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const headers: Record<string, string> = { 'Content-Type': 'application/json', 'Authorization': token };

  const base = API_BASE || window.location.origin;
  const url = new URL(base + path);
  if (params) Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));

  const res = await fetch(url.toString(), { method: 'PUT', headers, body: JSON.stringify(body) });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`PUT ${path} failed: ${res.status} ${text}`);
  }
  return res.json() as Promise<T>;
}

export async function apiDelete<T>(
  path: string,
  params?: Record<string, string>,
): Promise<T> {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const headers: Record<string, string> = { 'Content-Type': 'application/json', 'Authorization': token };

  const base = API_BASE || window.location.origin;
  const url = new URL(base + path);
  if (params) Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));

  const res = await fetch(url.toString(), { method: 'DELETE', headers });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`DELETE ${path} failed: ${res.status} ${text}`);
  }
  return res.json() as Promise<T>;
}

export async function apiUploadImage(file: File): Promise<string> {
  const token = await getAuthToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = token;

  const base64 = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(',')[1]); // strip data URL prefix
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const extension = file.name.split('.').pop() || 'jpg';

  const res = await fetch((API_BASE || window.location.origin) + '/api_upload_image', {
    method: 'POST',
    headers,
    body: JSON.stringify({ image: base64, extension }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Image upload failed: ${res.status} ${text}`);
  }
  const data = (await res.json()) as { imageUrl: string };
  return data.imageUrl;
}
